import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionOneImageOneTextApi from "hooks/api/private/blocks/useSectionOneImageOneTextApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionOneImageOneTextBody,
    SectionOneImageOneTextDetails,
} from "types/api/blocks/sectionOneImageOneText";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionOneImageOneTextForm = {
    id: string | undefined;
    img_id: string | undefined;
    img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            text: string;
        };
    };
};

type SectionOneImageOneTextType = {
    setMainImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneImageOneTextForm>;
};

function useSectionOneImageOneText(): SectionOneImageOneTextType {
    const {
        saveSectionOneImageOneText,
        getOneSectionOneImageOneText,
        deleteSectionOneImageOneText,
    } = useSectionOneImageOneTextApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_id: {
                    type: "simple",
                    validators: [],
                },
                img: notEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: SectionOneImageOneTextForm = React.useMemo(() => {
        return {
            id: undefined,
            img_id: undefined,
            img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    text: "",
                },
                en: {
                    text: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneImageOneTextForm>(
        schema,
        initialValues,
        getExtraErrorsDescription
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setMainImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [section, setSectionDetails] = React.useState<SectionOneImageOneTextDetails | undefined>(
        undefined
    );

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOneImageOneText(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionDetails, getOneSectionOneImageOneText, blockGroupCompositionId]);

    React.useEffect(() => {
        if (section) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = section.id;
                newForm.img_id = section.img_id;

                newForm.text.it.text = section.text.it.text ?? "";
                newForm.text.en.text = section.text.en.text ?? "";

                newForm.img = {
                    id: section.img.id,
                    desktop: createAttachmentType({
                        id: section.img.desktop.id,
                        label: "Desktop",
                        filename: section.img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: section.img.mobile.id,
                        label: "Mobile",
                        filename: section.img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, section]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionVideoSideBody: SectionOneImageOneTextBody = {
                    id: blockGroupCompositionId,
                    img: {
                        id: form.img.id,
                        desktop: converterAttachmentToFileWithPayload(form.img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img.mobile),
                    },
                    text: {
                        it: {
                            text: form.text.it.text,
                        },
                        en: {
                            text: form.text.en.text,
                        },
                    },
                };
                const promises: Promise<unknown>[] = [];

                const [apiCall] = saveSectionOneImageOneText(sectionVideoSideBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOneImageOneText,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        graphicalOrder,
        blockGroupId,
        blockId,
        isValid,
        form,
    ]);

    return React.useMemo(
        (): SectionOneImageOneTextType => ({
            buttonDelete: deleteSectionOneImageOneText,
            setMainImage,
            formHook,
        }),
        [deleteSectionOneImageOneText, setMainImage, formHook]
    );
}

export default useSectionOneImageOneText;
